import icons from '@/assets/icons';
import { QuestionnaireStatus } from '@/components/decision-tree';
import IconCatch from '@/components/icons/icon-catch/icon-catch';
import EditAnswerButton from '@/components/modal-edit-answer/edit-answer-button/edit-answer-button';
import ModalEditAnswer from '@/components/modal-edit-answer/modal-edit-answer';
import { Accordion, Box, Divider, Flex, IconAlertCircle, Text } from '@cmpsr/components';
import { Fragment } from 'react';
import RiskAssessmentStatusBadge from '../risk-assessment-status-badge/risk-assessment-status-badge';
import useRiskAssessmentSectionsController from './risk-assessment-sections.controller';
import Markdown from '@/components/markdown/markdown';

type RiskAssessmentSectionsProps = {
  questionnaireStatus: QuestionnaireStatus;
};

export default function RiskAssessmentSections({ questionnaireStatus }: RiskAssessmentSectionsProps) {
  const {
    riskAssessmentSections,
    getSectionData,
    getBadgeStatus,
    getBadgeText,
    sectionIcons,
    getSectionUnclearAnswers,
    showModalEditAnswer,
    onHideModalEditAnswer,
    selectedQuestionId,
    handleEditClick,
  } = useRiskAssessmentSectionsController();

  return (
    <>
      {questionnaireStatus !== QuestionnaireStatus.uncompleted && (
        <Accordion
          display="flex"
          flexDirection="column"
          flexGrow={1}
          gap={{ base: 'spacer-4', lg: 'spacer-6' }}
          allowToggle
        >
          {riskAssessmentSections?.map((section, index) => {
            const unclearAnswers = getSectionUnclearAnswers(section.id);
            const questionsData = getSectionData(section);
            const badgeStatus = getBadgeStatus(unclearAnswers);
            const badgeText = getBadgeText(unclearAnswers);
            const icon = sectionIcons[index] as keyof typeof icons;

            return (
              <Accordion.Item
                key={index}
                bg="background-light"
                borderRadius="radii-3"
                border="1px solid"
                borderColor="ui-element-divider"
                p="spacer-6"
              >
                <Accordion.Button p="0" _expanded={{ mb: { base: '4px', lg: '12px' } }}>
                  <Flex flex="1" alignItems="center" gap={{ base: 'spacer-4', lg: 'spacer-6' }}>
                    <IconCatch icon={icon} backgroundColor="background-page-03" width="40px" height="40px" />
                    <Flex
                      flexDirection={{ base: 'column', lg: 'row' }}
                      alignItems={{ base: 'flex-start', lg: 'center' }}
                      gap={{ base: 'spacer-2', lg: 'spacer-4' }}
                    >
                      <Text textStyle="text-header-m-v">{section.name}</Text>
                      {badgeStatus && (
                        <RiskAssessmentStatusBadge status={badgeStatus}>{badgeText}</RiskAssessmentStatusBadge>
                      )}
                    </Flex>
                  </Flex>
                  <Accordion.Icon />
                </Accordion.Button>
                <Accordion.Panel p="0">
                  {questionsData.map(({ surveyItemId, label, answerText, answerDate, isIDontKnowAnswer }) => (
                    <Fragment key={surveyItemId}>
                      <Divider mt="spacer-3" mb="spacer-3" />
                      <Flex>
                        <Box>
                          {isIDontKnowAnswer && <IconAlertCircle size="m" color="text-warning" mr="spacer-2" />}
                        </Box>
                        <Flex
                          flexDirection={{ base: 'column', lg: 'row' }}
                          alignItems={{ base: 'flex-start', lg: 'center' }}
                          flexGrow={1}
                          gap="spacer-1"
                        >
                          <Box flexGrow={1}>
                            <Box mr={{ base: 'none', lg: 'spacer-4' }}>
                              <Markdown>{label}</Markdown>
                            </Box>
                            {answerText && (
                              <Text variant="text-body-meta-regular" color="text-secondary">
                                {answerText}
                              </Text>
                            )}
                          </Box>
                          {answerDate && (
                            <Text
                              variant="text-body-floating-label-regular"
                              color="text-disabled"
                              maxWidth={{ base: 'unset', lg: '70px' }}
                              textAlign={{ base: 'left', lg: 'right' }}
                            >
                              Last&nbsp;update&nbsp;{answerDate}
                            </Text>
                          )}
                        </Flex>
                        <Box ml="spacer-4" mr="spacer-4">
                          <Divider orientation="vertical" height="100%" />
                        </Box>
                        <EditAnswerButton onClick={() => handleEditClick(surveyItemId)} />
                      </Flex>
                    </Fragment>
                  ))}
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
      {showModalEditAnswer && (
        <ModalEditAnswer isOpen onCloseClick={onHideModalEditAnswer} surveyItemId={selectedQuestionId} />
      )}
    </>
  );
}
